import React, { useState, useMemo } from 'react';
import { apiCall, getUser } from '../lib/api.js';
import { CATEGORIES, PROCESS_DEDUCTIONS, AUTOFAILS, RATING_OPTIONS, TOTAL_ATTRIBUTES, calculateScore } from '../lib/scorecard-config.js';

const CAT_STYLES = {
  'Verification & Account Handling': { border: 'border-l-emerald-500', icon: 'ti-shield-check', iconColor: 'text-emerald-600', badge: 'bg-emerald-50 text-emerald-700' },
  'Accuracy & Knowledge': { border: 'border-l-purple-500', icon: 'ti-book', iconColor: 'text-purple-600', badge: 'bg-purple-50 text-purple-700' },
  'Resolution & Effort': { border: 'border-l-blue-500', icon: 'ti-target', iconColor: 'text-blue-600', badge: 'bg-blue-50 text-blue-700' },
  'Empathy & Tone': { border: 'border-l-pink-500', icon: 'ti-heart', iconColor: 'text-pink-600', badge: 'bg-pink-50 text-pink-700' },
  'Communication & Clarity': { border: 'border-l-amber-500', icon: 'ti-message-dots', iconColor: 'text-amber-600', badge: 'bg-amber-50 text-amber-700' },
  'Responsiveness & Documentation': { border: 'border-l-cyan-500', icon: 'ti-clock', iconColor: 'text-cyan-600', badge: 'bg-cyan-50 text-cyan-700' },
};

const BAND_STYLES = {
  'Excellent': 'bg-emerald-50 text-emerald-700 border-emerald-200',
  'Good': 'bg-blue-50 text-blue-700 border-blue-200',
  'Okay': 'bg-amber-50 text-amber-700 border-amber-200',
  'Needs improvement': 'bg-orange-50 text-orange-700 border-orange-200',
  'Failed': 'bg-red-50 text-red-700 border-red-200',
};

const PILL = {
  'Meets': 'bg-emerald-50 text-emerald-700 border-emerald-300',
  'Partial': 'bg-amber-50 text-amber-700 border-amber-300',
  'Misses': 'bg-red-50 text-red-700 border-red-300',
  'N/A': 'bg-gray-100 text-gray-500 border-gray-200',
};

export default function ScorecardForm({ onSaved, editingEval, onCancelEdit, onViewEval }) {
  const user = getUser();
  const [step, setStep] = useState(editingEval ? 'form' : 'ticket');
  const [ticketNum, setTicketNum] = useState('');
  const [ticket, setTicket] = useState(editingEval ? { ticket_number: editingEval.ticket_number, subject: editingEval.ticket_subject, agent_name: editingEval.agent_name, customer_name: editingEval.customer_name, customer_email: '', message_count: 0, status: '', channel: '', tags: [] } : null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [messageType, setMessageType] = useState(editingEval?.message_type || 'email');
  const [interactionSummary, setInteractionSummary] = useState(editingEval?.interaction_summary || '');
  const [notes, setNotes] = useState(editingEval?.notes || '');
  const [scores, setScores] = useState(() => {
    if (editingEval?.scores) return { ...editingEval.scores };
    const defaults = {};
    CATEGORIES.forEach(cat => cat.attributes.forEach(attr => { defaults[attr.id] = attr.id === 'E4' ? 'N/A' : 'Meets'; }));
    return defaults;
  });
  const [deductions, setDeductions] = useState(editingEval?.deductions || []);
  const [autofails, setAutofails] = useState(editingEval?.autofails || []);

  const setScore = (id, val) => setScores(p => ({ ...p, [id]: val }));
  const toggleDed = (id) => setDeductions(p => p.includes(id) ? p.filter(x => x !== id) : [...p, id]);
  const toggleAf = (id) => setAutofails(p => p.includes(id) ? p.filter(x => x !== id) : [...p, id]);

  const result = useMemo(() => calculateScore(scores, deductions, autofails), [scores, deductions, autofails]);
  const allScored = result.scored === TOTAL_ATTRIBUTES;
  const progress = Math.round((result.scored / TOTAL_ATTRIBUTES) * 100);

  const [existingEval, setExistingEval] = useState(null);

  const fetchTicket = async () => {
    setLoading(true); setError(''); setExistingEval(null);
    try {
      // Check if ticket already scored
      const check = await apiCall('/evaluation/check?ticket=' + encodeURIComponent(ticketNum));
      if (check.exists) {
        // evaluation is null when another scorer authored it (server hides details)
        setExistingEval(check.evaluation || { restricted: true, evaluator_type: check.evaluator_type });
        setLoading(false);
        return;
      }
      // Not scored yet — fetch from the CRM
      const data = await apiCall('/ticket/fetch', { method: 'POST', body: { ticket_number: ticketNum } });
      setTicket(data); setStep('form');
    } catch (e) { setError(e.message); }
    setLoading(false);
  };

  const handleSave = async () => {
    if (!allScored) return;
    setLoading(true); setError('');
    try {
      await apiCall(editingEval ? '/evaluation/update' : '/evaluation/save', { method: 'POST', body: {
        evaluation_id: editingEval?.id,
        eval_date: new Date().toLocaleDateString('en-CA'),
        ticket_number: ticket.ticket_number, agent_name: ticket.agent_name, company: ticket.company,
        evaluator_email: user?.email || '', scores, deductions, autofails,
        autofail_triggered: result.hasAutofail, base_score: result.baseScore,
        deduction_total: result.deductionTotal, final_score: result.finalScore,
        rating_band: result.ratingBand, notes, ticket_subject: ticket.subject,
        customer_name: ticket.customer_name, message_type: messageType,
        interaction_summary: interactionSummary,
      }});
      if (onSaved) onSaved();
      resetForm();
      alert((editingEval ? 'Evaluation updated' : 'Evaluation saved') + '! Score: ' + Math.round(result.finalScore * 100) + '% (' + result.ratingBand + ')');
    } catch (e) { setError(e.message); }
    setLoading(false);
  };

  const resetForm = () => {
    if (onCancelEdit) { onCancelEdit(); return; }
    setStep('ticket'); setTicket(null); setTicketNum(''); const defs = {}; CATEGORIES.forEach(cat => cat.attributes.forEach(attr => { defs[attr.id] = attr.id === 'E4' ? 'N/A' : 'Meets'; })); setScores(defs);
    setDeductions([]); setAutofails([]); setNotes(''); setInteractionSummary('');
  };

  if (step === 'ticket') return (
    <div>
      <h2 className="text-lg font-medium mb-4">New evaluation</h2>
      {error && <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-4 text-sm">{error}</div>}
      <div className="bg-white rounded-xl border border-gray-200 p-5">
        <label className="block text-sm text-gray-500 mb-2">Ticket number</label>
        {existingEval && (
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-4">
            <div className="flex items-start gap-3">
              <i className="ti ti-alert-triangle text-amber-500 text-lg mt-0.5" aria-hidden="true"></i>
              <div className="flex-1">
                <div className="text-sm font-medium text-amber-800">Ticket #{ticketNum} has already been evaluated</div>
                <div className="text-xs text-amber-600 mt-1">
                  {existingEval.restricted
                    ? 'Scored by another QA scorer'
                    : <>Score: {Math.round((existingEval.final_score || 0) * 100)}% ({existingEval.rating_band}) &middot; Agent: {existingEval.agent_name} &middot; {existingEval.eval_date}</>}
                </div>
                <div className="flex gap-2 mt-3">
                  {!existingEval.restricted && (
                  <button onClick={() => { if (onViewEval) onViewEval(existingEval); }}
                    className="px-3 py-1.5 bg-amber-600 text-white rounded-lg text-xs font-medium hover:bg-amber-700">
                    View scorecard
                  </button>
                  )}
                  <button onClick={() => { setExistingEval(null); }}
                    className="px-3 py-1.5 bg-white text-amber-700 border border-amber-300 rounded-lg text-xs font-medium hover:bg-amber-50">
                    Score again anyway
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
        <div className="flex gap-3">
          <div className="flex-1 relative">
            <i className="ti ti-ticket absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" aria-hidden="true"></i>
            <input type="text" value={ticketNum} onChange={e => setTicketNum(e.target.value)}
              placeholder="e.g. 248064987"
              className="w-full pl-9 pr-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none"
              onKeyDown={e => e.key === 'Enter' && ticketNum && fetchTicket()} />
          </div>
          <button onClick={fetchTicket} disabled={!ticketNum || loading}
            className="px-5 py-2.5 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:bg-gray-300 transition text-sm font-medium">
            {loading ? 'Loading...' : 'Fetch ticket'}
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <div>
      {error && <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-4 text-sm">{error}</div>}

      {/* TICKET BAR */}
      <div className="bg-white rounded-xl border border-gray-200 p-4 mb-4">
        <div className="flex flex-wrap items-start gap-3">
          <i className="ti ti-ticket text-gray-400 text-lg mt-0.5" aria-hidden="true"></i>
          <div className="flex-1 min-w-0">
            <div className="font-medium text-sm">#{ticket.ticket_number} <span className="font-normal text-gray-500">—</span> <span className="font-normal text-gray-600">{ticket.subject}</span></div>
            <div className="flex flex-wrap gap-x-4 gap-y-1 mt-1.5 text-xs text-gray-500">
              <span><i className="ti ti-user text-xs mr-1" aria-hidden="true"></i>{ticket.customer_name}</span>
              <span><i className="ti ti-mail text-xs mr-1" aria-hidden="true"></i>{ticket.customer_email}</span>
              <span><i className="ti ti-messages text-xs mr-1" aria-hidden="true"></i>{ticket.message_count} messages</span>
              <span><i className="ti ti-circle-dot text-xs mr-1" aria-hidden="true"></i>{ticket.status}</span>
              {ticket.channel && <span><i className="ti ti-device-desktop text-xs mr-1" aria-hidden="true"></i>{ticket.channel}</span>}
            </div>
            {ticket.tags?.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mt-2">
                {ticket.tags.map(t => <span key={t} className="px-2 py-0.5 bg-gray-100 text-gray-600 rounded text-[11px]">{t}</span>)}
              </div>
            )}
          </div>
          <span className="px-3 py-1 bg-purple-50 text-purple-700 rounded-full text-xs font-medium">{ticket.agent_name}</span>
          <button onClick={resetForm} className="text-xs text-gray-400 hover:text-gray-600"><i className="ti ti-x" aria-hidden="true"></i></button>
        </div>
      </div>

      {/* META ROW + INLINE SCORE */}
      <div className="grid grid-cols-1 sm:grid-cols-[auto_1fr_auto] gap-3 mb-5">
        <div className="bg-white rounded-xl border border-gray-200 px-4 py-3">
          <label className="block text-xs text-gray-400 mb-1">Message type</label>
          <select value={messageType} onChange={e => setMessageType(e.target.value)}
            className="w-full text-sm border-0 p-0 focus:ring-0 bg-transparent">
            <option value="email">Email</option><option value="chat">Chat</option><option value="phone">Phone</option><option value="social">Social</option>
          </select>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 px-4 py-3">
          <label className="block text-xs text-gray-400 mb-1">Interaction summary</label>
          <textarea value={interactionSummary} onChange={e => setInteractionSummary(e.target.value)}
            rows={3} placeholder="Describe the interaction..."
            className="w-full text-sm border-0 p-0 focus:ring-0 bg-transparent resize-none" />
        </div>
        {/* SCORE BOX — inline in the grid on desktop, visible always */}
        <div className="bg-white rounded-xl border border-gray-200 px-4 py-3 flex flex-col items-center justify-center min-w-[140px] sm:row-span-1">
          <div className={'text-3xl font-medium ' + (result.hasAutofail ? 'text-red-500' : result.scored > 0 ? 'text-gray-900' : 'text-gray-300')}>
            {result.scored > 0 ? Math.round(result.finalScore * 100) : '--'}
            <span className="text-sm text-gray-400">%</span>
          </div>
          <div className="text-[11px] text-gray-400">{result.scored}/{TOTAL_ATTRIBUTES} scored</div>
          {result.scored > 0 && (
            <span className={'text-[11px] font-medium px-2 py-0.5 rounded-full border mt-1 ' + (BAND_STYLES[result.ratingBand] || '')}>{result.ratingBand}</span>
          )}
        </div>
      </div>

      {/* STICKY FLOATING SAVE + SCORE (appears when scrolling) */}
      <div className="fixed bottom-4 right-4 z-30 flex items-center gap-3 bg-white rounded-xl border border-gray-200 shadow-lg px-4 py-3">
        <div className="h-1.5 w-20 bg-gray-100 rounded-full overflow-hidden">
          <div className="h-full bg-purple-500 rounded-full transition-all duration-500" style={{ width: progress + '%' }}></div>
        </div>
        <div className={'text-xl font-medium ' + (result.hasAutofail ? 'text-red-500' : result.scored > 0 ? 'text-gray-900' : 'text-gray-300')}>
          {result.scored > 0 ? Math.round(result.finalScore * 100) : '--'}%
        </div>
        {result.scored > 0 && <span className={'text-[11px] font-medium px-2 py-0.5 rounded-full border ' + (BAND_STYLES[result.ratingBand] || '')}>{result.ratingBand}</span>}
        {result.hasAutofail && <span className="text-[11px] text-red-600 font-medium">Autofail</span>}
        {allScored && (
          <button onClick={handleSave} disabled={loading}
            className="px-4 py-1.5 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:bg-gray-300 text-xs font-medium transition flex items-center gap-1.5">
            <i className="ti ti-device-floppy text-sm" aria-hidden="true"></i>
            {loading ? 'Saving...' : 'Save'}
          </button>
        )}
      </div>

      {/* CATEGORIES */}
      <div className="space-y-4">
        {CATEGORIES.map(cat => {
          const s = CAT_STYLES[cat.name] || {};
          const catWeight = cat.attributes.reduce((sum, a) => sum + a.weight, 0);
          return (
            <div key={cat.name} className={'bg-white rounded-xl border border-gray-200 overflow-hidden border-l-[3px] ' + (s.border || 'border-l-gray-300')}>
              <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
                <h3 className="text-sm font-medium flex items-center gap-2">
                  <i className={'ti ' + (s.icon || '') + ' ' + (s.iconColor || '')} aria-hidden="true"></i>
                  {cat.name}
                </h3>
                <span className={'text-[11px] font-medium px-2 py-0.5 rounded-full ' + (s.badge || 'bg-gray-100 text-gray-600')}>{catWeight} pts</span>
              </div>
              {cat.attributes.map(attr => (
                <div key={attr.id} className="px-4 py-3 border-b border-gray-50 last:border-0">
                  <div className="flex flex-col sm:flex-row sm:items-center gap-2">
                    <div className="flex-1">
                      <span className="text-sm text-gray-700">{attr.label}</span>
                      <span className="text-[11px] text-gray-400 ml-1">({attr.weight})</span>
                    </div>
                    <div className="flex gap-1 flex-shrink-0">
                      {RATING_OPTIONS.map(opt => (
                        <button key={opt} onClick={() => setScore(attr.id, opt)}
                          className={'px-2.5 py-1 rounded-full text-[11px] font-medium border transition '
                            + (scores[attr.id] === opt ? PILL[opt] : 'border-gray-200 text-gray-400 hover:border-gray-300 hover:text-gray-600')}>
                          {opt}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          );
        })}

        {/* DEDUCTIONS */}
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <h3 className="text-sm font-medium flex items-center gap-2 mb-1">
            <i className="ti ti-alert-triangle text-amber-500" aria-hidden="true"></i> Process deductions
          </h3>
          <p className="text-[11px] text-gray-400 mb-3">Each lowers final score by 5%</p>
          {PROCESS_DEDUCTIONS.map(pd => (
            <label key={pd.id} className="flex items-center gap-2.5 py-2 cursor-pointer">
              <input type="checkbox" checked={deductions.includes(pd.id)} onChange={() => toggleDed(pd.id)}
                className="w-3.5 h-3.5 rounded border-gray-300 text-amber-500 focus:ring-amber-500" />
              <span className="text-sm text-gray-600 flex-1">{pd.label}</span>
              <span className="text-[11px] text-amber-600 font-medium">-{pd.value * 100}%</span>
            </label>
          ))}
        </div>

        {/* AUTOFAILS */}
        <div className="bg-white rounded-xl border border-gray-200 border-l-[3px] border-l-red-400 p-4">
          <h3 className="text-sm font-medium text-red-700 flex items-center gap-2 mb-1">
            <i className="ti ti-alert-octagon" aria-hidden="true"></i> Behavioral autofails
          </h3>
          <p className="text-[11px] text-gray-400 mb-3">Any checked = automatic 0%</p>
          {AUTOFAILS.map(af => (
            <label key={af.id} className="flex items-center gap-2.5 py-2 cursor-pointer">
              <input type="checkbox" checked={autofails.includes(af.id)} onChange={() => toggleAf(af.id)}
                className="w-3.5 h-3.5 rounded border-gray-300 text-red-500 focus:ring-red-500" />
              <span className="text-sm text-gray-600">{af.label}</span>
            </label>
          ))}
        </div>

        {/* NOTES */}
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <h3 className="text-sm font-medium flex items-center gap-2 mb-3">
            <i className="ti ti-notes text-gray-400" aria-hidden="true"></i> Evaluator notes
          </h3>
          <textarea value={notes} onChange={e => setNotes(e.target.value)} rows={3}
            placeholder="Overall observations, coaching notes..."
            className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none resize-none" />
        </div>

        <div className="flex justify-end gap-3 pb-24 lg:pb-8">
          <button onClick={resetForm} className="px-5 py-2.5 bg-gray-100 text-gray-600 rounded-lg hover:bg-gray-200 transition text-sm font-medium">Cancel</button>
          <button onClick={handleSave} disabled={!allScored || loading}
            className="px-5 py-2.5 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:bg-gray-300 transition text-sm font-medium flex items-center gap-2">
            <i className="ti ti-device-floppy" aria-hidden="true"></i>
            {loading ? 'Saving...' : 'Save evaluation'}
          </button>
        </div>
      </div>
    </div>
  );
}
